/*
 * AONSK V11.48 search-intent and destination router
 *
 * Preserves V11.47 strict title-field filtering for mutually exclusive
 * product types, then adds exact destination routing for customer queries
 * that clearly name an existing collection or seasonal landing page.
 *
 * Exact destination routes are intentionally narrow. Longer or cultivar-
 * specific searches continue through Shopify's normal search experience.
 */
(() => {
  if (window.__aonskSearchIntentRouterV1148) return;
  window.__aonskSearchIntentRouterV1148 = true;

  const normalize = (value) => String(value || '')
    .toLowerCase()
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const strictMappings = new Map();
  const destinationMappings = new Map();

  const addStrict = (queries, strictQuery) => {
    queries.forEach((query) => strictMappings.set(normalize(query), strictQuery));
  };

  const addDestination = (queries, destination) => {
    queries.forEach((query) => destinationMappings.set(normalize(query), destination));
  };

  // High-volume navigation queries with strong live collection destinations.
  addDestination([
    'fall garden', 'fall gardens', 'fall garden seeds', 'fall planting',
    'fall seeds', 'autumn garden', 'autumn gardens', 'autumn planting',
    'autumn seeds', 'seeds for fall', 'fall vegetable seeds'
  ], '/collections/fall-garden');

  addDestination([
    'tea garden', 'tea gardens', 'tea garden seeds', 'tea seeds',
    'herbal tea seeds', 'herb tea seeds'
  ], '/collections/tea-garden');

  addDestination([
    'pollinator garden', 'pollinator gardens', 'pollinator seeds',
    'pollinator flower seeds', 'butterfly garden', 'butterfly gardens'
  ], '/collections/pollinator-patch');

  addDestination([
    'seed starting', 'seed starting supplies', 'seed starter supplies',
    'seed starting equipment', 'starting seeds', 'seed trays',
    'seedling trays', 'grow lights'
  ], '/collections/seed-starting-supplies');

  addDestination([
    'seed potato', 'seed potatoes', 'potato seed', 'potato seeds',
    'potato', 'potatoes'
  ], '/collections/seed-potatoes');

  addDestination([
    'garlic', 'garlic seed', 'garlic seeds', 'seed garlic',
    'garlic bulb', 'garlic bulbs'
  ], '/collections/garlic-bulbs');

  addDestination([
    'strawberry', 'strawberries', 'strawberry seed', 'strawberry seeds'
  ], '/collections/strawberry-seeds');

  addDestination([
    'heirloom seed', 'heirloom seeds', 'heritage seed', 'heritage seeds'
  ], '/collections/all-heirloom-seeds');

  addDestination([
    'vegetable seed', 'vegetable seeds', 'garden vegetable seeds'
  ], '/collections/all-vegetable-seeds');

  addDestination(['flower seed', 'flower seeds'], '/collections/all-flower-seeds');
  addDestination(['herb seed', 'herb seeds'], '/collections/all-herb-seeds');
  addDestination([
    'cover crop', 'cover crops', 'cover crop seed', 'cover crop seeds'
  ], '/collections/cover-crop-seeds');
  addDestination(['native seed', 'native seeds'], '/collections/native-seeds');

  // V11.47 strict product-type mappings retained unchanged.
  addStrict([
    'determinate tomato', 'determinate tomatoes', 'determinate tomato seed',
    'determinate tomato seeds', 'tomato determinate', 'tomatoes determinate',
    'tomato seed determinate', 'tomato seeds determinate', 'bush tomato',
    'bush tomatoes', 'bush tomato seed', 'bush tomato seeds'
  ], 'title:tomato title:determinate');

  addStrict([
    'indeterminate tomato', 'indeterminate tomatoes',
    'indeterminate tomato seed', 'indeterminate tomato seeds',
    'tomato indeterminate', 'tomatoes indeterminate',
    'tomato seed indeterminate', 'tomato seeds indeterminate',
    'vining tomato', 'vining tomatoes', 'vining tomato seed',
    'vining tomato seeds'
  ], 'title:tomato title:indeterminate');

  addStrict([
    'edamame', 'edamame seed', 'edamame seeds', 'soybean', 'soybeans',
    'soybean seed', 'soybean seeds', 'soy bean', 'soy beans',
    'soy bean seed', 'soy bean seeds'
  ], 'title:edamame');

  addStrict([
    'cowpea', 'cowpeas', 'cowpea seed', 'cowpea seeds', 'southern pea',
    'southern peas', 'southern pea seed', 'southern pea seeds',
    'black eyed pea', 'black eyed peas', 'black eyed pea seed',
    'black eyed pea seeds', 'black-eye pea', 'black-eye peas',
    'black-eyed pea', 'black-eyed peas'
  ], 'title:cowpeas');

  addStrict([
    'scallion', 'scallions', 'scallion seed', 'scallion seeds',
    'green onion', 'green onions', 'green onion seed', 'green onion seeds',
    'bunching onion', 'bunching onions', 'bunching onion seed',
    'bunching onion seeds', 'spring onion', 'spring onions'
  ], 'title:bunching title:onion');

  addStrict([
    'luffa', 'luffa seed', 'luffa seeds', 'loofah', 'loofah seed',
    'loofah seeds', 'loofa', 'loofa seed', 'loofa seeds', 'sponge gourd',
    'sponge gourd seed', 'sponge gourd seeds'
  ], 'title:luffa');

  const redirectCurrentSearchIfNeeded = () => {
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    if (path !== '/search') return false;

    const url = new URL(window.location.href);
    const destination = destinationMappings.get(normalize(url.searchParams.get('q')));
    if (!destination) return false;

    window.location.replace(destination);
    return true;
  };

  if (redirectCurrentSearchIfNeeded()) return;

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('form[role="search"], form.search')) return;

    const input = form.querySelector('input[name="q"]');
    if (!input) return;

    const normalizedQuery = normalize(input.value);
    const destination = destinationMappings.get(normalizedQuery);
    if (destination) {
      event.preventDefault();
      window.location.assign(destination);
      return;
    }

    const strictQuery = strictMappings.get(normalizedQuery);
    if (!strictQuery) return;

    input.value = strictQuery;

    let typeInput = form.querySelector('input[name="type"]');
    if (!typeInput) {
      typeInput = document.createElement('input');
      typeInput.type = 'hidden';
      typeInput.name = 'type';
      form.appendChild(typeInput);
    }
    typeInput.value = 'product';
  }, true);
})();
